import { toast } from 'vue-sonner'
import { USER_BETA_FEATURES } from '@/constants'
import { useMsgBox } from '@deepcloud/deep-ui-lib'
import { useApi } from './api'
import { useKeycloak } from './auth/keycloak'
import { useNodePolicy } from './node/node-policy'
import { useOrganization } from './organization/organization'
import { useOrganizationRoles } from './organization/organization-roles'
import { isMimeTypeEditSupported } from './preview/preview'
import type { Node, PathSegment } from '@deepcloud/api-deep-box'

export function useOffice() {
  const { hasBetaFeature: hasBetaFeatureUser } = useKeycloak()
  const router = useRouter()
  const { t } = useI18n()
  const deepMsgBox = useMsgBox()
  const { currentContextOrgId, hasBetaFeature } = useOrganization()
  const { isOwner, isOwnerOrAdmin } = useOrganizationRoles()
  const { user } = useKeycloak()
  const { deepAdminBackendAPI } = useApi()

  const isOnlyOfficeEnabled = computed(
    () =>
      import.meta.env.VITE_ONLY_OFFICE_ENABLED === 'true' ||
      hasBetaFeatureUser(USER_BETA_FEATURES.USER_BETA_FEATURE_ONLYOFFICE),
  )

  const isCloudOfficeEnabled = computed(
    () =>
      import.meta.env.VITE_CLOUD_OFFICE_ENABLED === 'true' ||
      hasBetaFeature('cloudoffice'),
  )

  const isCloudOfficeOrOnlyOfficeEnabled = computed(
    () => isOnlyOfficeEnabled.value || isCloudOfficeEnabled.value,
  )

  async function showMsgOwner() {
    const msgOptions = {
      title: t('node.cloudoffice.msg_box.terms.owner.title'),
      content: t('node.cloudoffice.msg_box.terms.owner.content'),
      msgBoxType: 'checkbox',
      msgBoxTypeFormFieldProps: {
        label: t('node.cloudoffice.msg_box.terms.owner.checkbox.label'),
      },
      btnActions: {
        confirm: {
          text: t('buttons.proceed'),
        },
      },
    }

    const { action, checkbox } = await deepMsgBox(msgOptions)

    const accepted = action === 'confirm' && checkbox

    if (!accepted || !currentContextOrgId.value) return false

    try {
      await deepAdminBackendAPI.value.termsOfUseDeclarations.create({
        type: 'microsoft_cspp_wopi',
        status: 'accepted',
        orgId: currentContextOrgId.value,
        subject: { userId: user.value?.sub ?? '' },
      })

      return accepted
    } catch {
      toast.error(t('error.error_occurred'))
      return false
    }
  }

  async function showMsgMember() {
    const msgOptions = {
      title: t('node.cloudoffice.msg_box.terms.member.title'),
      content: t('node.cloudoffice.msg_box.terms.member.content'),
    }

    await deepMsgBox.warning(msgOptions)
  }

  const { canRevision } = useNodePolicy()

  function canUseNodeOffice(node: Node | PathSegment) {
    return (
      canRevision(node) &&
      isMimeTypeEditSupported(node?.mimeType) &&
      ((isCloudOfficeEnabled.value && isOwnerOrAdmin.value) ||
        isOnlyOfficeEnabled.value)
    )
  }

  async function canUseCloudoffice() {
    if (!isCloudOfficeEnabled.value) return false
    if (!currentContextOrgId.value) return false

    try {
      const res = await deepAdminBackendAPI.value.termsOfUseDeclarations.get({
        termsOfUseType: 'microsoft_cspp_wopi',
        orgId: currentContextOrgId.value,
      })

      // sort newest first
      const items = res.data.items?.sort((x, y) => {
        if (!x?.createdAt || !y?.createdAt) return 0
        return new Date(y.createdAt).getTime() - new Date(x.createdAt).getTime()
      })

      // return status of the "newest" item
      if (items?.length && items[0].status === 'accepted') return true

      // show msg if the "newest" item was not 'accepted'
      if (isOwner.value) {
        return showMsgOwner()
      } else {
        return showMsgMember()
      }
    } catch {
      toast.error(t('error.error_occurred'))
      return false
    }
  }

  async function getOfficeEditNodeRoute(nodeId: string) {
    if (isCloudOfficeEnabled.value) {
      // if cloudoffice is enabled, check if user has accepted the terms
      // if yes, open it
      // if no, show MsgBox with Info about it
      const can = await canUseCloudoffice()
      if (!can) return
      return {
        name: 'node-edit-cloudoffice',
        params: { node: nodeId },
        query: { mode: 'edit' },
      }
    } else if (isOnlyOfficeEnabled.value) {
      return {
        name: 'node-edit',
        params: { node: nodeId },
        query: { mode: 'edit' },
      }
    }
    return undefined
  }

  async function getOfficeEditNodeUrl(nodeId: string) {
    const routeOffice = await getOfficeEditNodeRoute(nodeId)
    if (!routeOffice) return undefined

    const routeData = router.resolve(routeOffice)

    if (!routeData) return undefined

    return routeData.href
  }

  async function openOfficeEditNodeUrl(node: Node | PathSegment) {
    if (!canUseNodeOffice(node)) return
    const url = await getOfficeEditNodeUrl(node.nodeId)
    if (!url) return undefined

    return window.open(url, '_blank')
  }

  return {
    isOnlyOfficeEnabled,
    isCloudOfficeEnabled,
    isCloudOfficeOrOnlyOfficeEnabled,
    openOfficeEditNodeUrl,
    canUseNodeOffice,
  }
}
